import { useCallback, useEffect, useRef, useState } from 'react'
import { useCheckUserTokenMutation, useGetUserAuthorizeUrlMutation } from './accountApi'
const MAX_ATTEMPTS = 5
const POPUP_AUTO_CLOSE_MS = 10000
const POPUP_POLL_MS = 500

export const AUTHORIZE_STAGE = {
    IDLE: 'idle',
    CHECKING: 'checking',
    AUTHORIZING: 'authorizing',
    RUNNING: 'running'
}
const waitForPopup = (popup) => new Promise((resolve) => {
    const startedAt = Date.now()
    const timer = setInterval(() => {
        if (popup.closed || Date.now() - startedAt >= POPUP_AUTO_CLOSE_MS) {
            clearInterval(timer)
            if (!popup.closed) {
                popup.close()
            }
            resolve()
        }
    }, POPUP_POLL_MS)
})
const useAuthorizedAction = () => {
    const [checkUserToken] = useCheckUserTokenMutation()
    const [getAuthorizeUrl] = useGetUserAuthorizeUrlMutation()
    const [stage, setStage] = useState(AUTHORIZE_STAGE.IDLE)
    const isMounted = useRef(true)

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    const setStageIfMounted = (next) => {
        if (isMounted.current) {
            setStage(next)
        }
    }

    const run = useCallback(async (action) => {
        try {
            for (let attempt = 1; attempt <= MAX_ATTEMPTS && isMounted.current; attempt += 1) {
                setStageIfMounted(AUTHORIZE_STAGE.CHECKING)
                const check = await checkUserToken().unwrap()
                if (!check || check.result !== false) {
                    setStageIfMounted(AUTHORIZE_STAGE.RUNNING)
                    return await action()
                }

                setStageIfMounted(AUTHORIZE_STAGE.AUTHORIZING)
                const url = await getAuthorizeUrl().unwrap()
                const popup = window.open(url, '', 'width=500,height=500')
                if (!popup) {
                    throw new Error('Allow pop-ups for this site to authorize, then try again.')
                }
                await waitForPopup(popup)
            }
            throw new Error('Authentication failed: the request could not be completed.')
        } catch (error) {
            if (error instanceof Error) {
                throw error
            }
            throw new Error('The request could not be completed. Please try again.')
        } finally {
            setStageIfMounted(AUTHORIZE_STAGE.IDLE)
        }
    }, [checkUserToken, getAuthorizeUrl])

    return { run, stage, isBusy: stage !== AUTHORIZE_STAGE.IDLE }
}

export default useAuthorizedAction
